import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import { DeviceIconDisplay, DeviceIconPicker, isCustomIcon, IconColorPicker, IconSearchInput, assetTypeIcon, formatIconLabel } from '../icons/DeviceIcons';
import TimezoneSelector from '../TimezoneSelector';
import { Loader2, Save, Clock } from 'lucide-react';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { deviceDisplayName } from '../../utils/deviceName';

export const assetTypes = [
  { value: 'car', label: 'Car' }, { value: 'suv', label: 'SUV' },
  { value: 'van', label: 'Van' },
  { value: 'ute', label: 'Ute' }, { value: 'truck', label: 'Truck' },
  { value: 'bus', label: 'Bus' },
  { value: 'motorcycle', label: 'Motorcycle' }, { value: 'trailer', label: 'Trailer' },
  { value: 'machine', label: 'Machine' }, { value: 'excavator', label: 'Excavator' },
  { value: 'ebike', label: 'eBike' }, { value: 'scooter', label: 'Scooter' },
  { value: 'caravan', label: 'Caravan' }, { value: 'boat', label: 'Boat' },
  { value: 'jetski', label: 'JetSki' }, { value: 'container', label: 'Container' },
  { value: 'tool', label: 'Tool' }, { value: 'equipment', label: 'Equipment' },
  { value: 'pallet', label: 'Pallet' }, { value: 'cargo', label: 'Cargo' },
  { value: 'temperature_zone', label: 'Temperature Zone' }, { value: 'door', label: 'Door / Gate' },
  { value: 'building', label: 'Building / Property' },
  { value: 'personnel', label: 'Personnel' }, { value: 'device', label: 'Device' },
  { value: 'other', label: 'Other' },
];

export const beaconAssetTypes = [
  { value: 'people', label: 'People' }, { value: 'trailer', label: 'Trailer' },
  { value: 'container', label: 'Container' }, { value: 'tool', label: 'Tool' },
  { value: 'equipment', label: 'Equipment' }, { value: 'pallet', label: 'Pallet' },
  { value: 'cargo', label: 'Cargo' }, { value: 'temperature_zone', label: 'Temperature Zone' },
  { value: 'door', label: 'Door / Gate' }, { value: 'vehicle', label: 'Vehicle' },
  { value: 'other', label: 'Other' },
];

// Display label for an asset_type value (e.g. 'van' → 'Van', 'suv' → 'SUV').
export const assetTypeLabel = (v) => {
  const m = [...assetTypes, ...beaconAssetTypes].find((t) => t.value === v);
  return m ? m.label : (v || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const fuelTypes = [
  { value: 'petrol', label: 'Petrol' }, { value: 'diesel', label: 'Diesel' },
  { value: 'electric', label: 'Electric' }, { value: 'hybrid', label: 'Hybrid' },
  { value: 'lpg', label: 'LPG' }, { value: 'na', label: 'N/A' },
];

const VEHICLE_TYPES = ['car', 'suv', 'van', 'ute', 'truck', 'bus', 'trailer', 'motorcycle', 'machine', 'excavator', 'scooter', 'caravan', 'boat', 'jetski'];
const ODO_TYPES = [...VEHICLE_TYPES, 'ebike'];

export const EditDeviceDialog = ({ open, onOpenChange, device, api, onSaved }) => {
  const [activeTab, setActiveTab] = useState('asset');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({});
  // Fresh device data fetched on each open
  const [freshDevice, setFreshDevice] = useState(null);
  const [customIcons, setCustomIcons] = useState([]);
  const [iconPickerSearch, setIconPickerSearch] = useState('');
  const { isFeatureEnabled } = useFeatureFlags();

  // Dual check: user privilege AND device-level setting
  const userCanEditAsset = isFeatureEnabled('live_tracking_edit_asset');
  const userCanEditIcon = isFeatureEnabled('live_tracking_edit_icon');
  const showAssetTab = userCanEditAsset && device?.asset_editing_enabled !== false;
  const showIconTab = userCanEditIcon && device?.icon_editing_enabled !== false;
  const isBeacon = device?.device_type === 'beacon';

  // Fetch custom icons when dialog opens
  useEffect(() => {
    if (open && api) {
      api.get('/custom-icons').then(res => setCustomIcons(res.data || [])).catch(() => {});
    }
  }, [open, api]);

  // Fetch fresh device data every time the dialog opens
  const fetchFreshDevice = useCallback(async () => {
    if (!device?.id || !api) return;
    setLoading(true);
    try {
      // Use /gps/live which works for all users and includes computed fields
      const res = await api.get('/gps/live');
      const d = res.data.find(dev => dev.id === device.id);
      if (!d) { setLoading(false); return; }
      setFreshDevice(d);

      // Compute live odometer: base + accumulated GPS distance
      let liveOdometer = d.odometer_reading || 0;
      if (d.odometer_source === 'gps') {
        liveOdometer = (d.odometer_reading || 0) + (d.gps_odometer || 0);
      } else if (d.odometer_source === 'sensor') {
        liveOdometer = d.sensor_odometer || d.computed_odometer || 0;
      }

      // Compute live engine hours: base + accumulated ignition time
      let liveEngineHours = d.engine_hours || 0;
      if (d.engine_hours_source === 'ignition') {
        liveEngineHours = (d.engine_hours || 0) + (d.ignition_engine_hours || 0);
      } else if (d.engine_hours_source === 'sensor') {
        liveEngineHours = d.sensor_engine_hours || d.computed_engine_hours || 0;
      }

      setForm({
        asset_name: d.asset_name || '',
        asset_type: d.asset_type || 'car',
        asset_make: d.asset_make || '',
        asset_model: d.asset_model || '',
        asset_year: d.asset_year || '',
        registration: d.registration || '',
        vin_number: d.vin_number || '',
        fuel_type: d.fuel_type || '',
        tank_capacity: d.tank_capacity ?? '',
        battery_capacity_kwh: d.battery_capacity_kwh ?? '',
        fuel_reference_consumption: d.fuel_reference_consumption ?? '',
        fuel_price: d.fuel_price ?? '',
        serial_no: d.serial_no || '',
        container_number: d.container_number || '',
        container_size: d.container_size || '',
        container_type: d.container_type || '',
        asset_description: d.asset_description || '',
        odometer_reading: liveOdometer ? Math.round(liveOdometer * 10) / 10 : 0,
        engine_hours: liveEngineHours ? Math.round(liveEngineHours * 10) / 10 : 0,
        _loaded_odometer: liveOdometer ? Math.round(liveOdometer * 10) / 10 : 0,
        _loaded_engine_hours: liveEngineHours ? Math.round(liveEngineHours * 10) / 10 : 0,
        device_icon: d.device_icon || 'car',
        device_icon_color: d.device_icon_color || null,
        map_marker_mode: d.map_marker_mode || 'arrow',
        timezone_override_enabled: d.timezone_override_enabled || false,
        timezone_override: d.timezone_override || 'auto',
      });
      setActiveTab(showAssetTab ? 'asset' : 'icon');
    } catch (err) {
      console.error('Failed to fetch device:', err);
    } finally {
      setLoading(false);
    }
  }, [device?.id, api, showAssetTab]);

  useEffect(() => {
    if (open && device?.id) {
      fetchFreshDevice();
    }
  }, [open, device?.id, fetchFreshDevice]);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const d = freshDevice || device;
      const payload = {};
      if (showAssetTab) {
        payload.asset_name = form.asset_name;
        payload.asset_type = form.asset_type;
        payload.asset_make = form.asset_make;
        payload.asset_model = form.asset_model;
        payload.asset_year = form.asset_year;
        payload.registration = form.registration;
        payload.vin_number = form.vin_number;
        payload.fuel_type = form.fuel_type || null;
        const numOrNull = (v) => (v === '' || v == null ? null : (Number.isNaN(parseFloat(v)) ? null : parseFloat(v)));
        payload.tank_capacity = numOrNull(form.tank_capacity);
        payload.battery_capacity_kwh = numOrNull(form.battery_capacity_kwh);
        payload.fuel_reference_consumption = numOrNull(form.fuel_reference_consumption);
        payload.fuel_price = numOrNull(form.fuel_price);
        payload.serial_no = form.serial_no;
        payload.container_number = form.container_number;
        payload.container_size = form.container_size;
        payload.container_type = form.container_type;
        payload.asset_description = form.asset_description;
        // Only send odometer/engine hours if user actually changed the value
        const currentOdo = parseFloat(form.odometer_reading) || 0;
        const currentEh = parseFloat(form.engine_hours) || 0;
        if (currentOdo !== (form._loaded_odometer || 0)) {
          payload.odometer_reading = currentOdo;
        }
        if (currentEh !== (form._loaded_engine_hours || 0)) {
          payload.engine_hours = currentEh;
        }
        payload.timezone_override_enabled = form.timezone_override_enabled;
        payload.timezone_override = form.timezone_override_enabled ? form.timezone_override : null;
      }
      if (showIconTab) {
        payload.device_icon = form.device_icon;
        payload.device_icon_color = form.device_icon_color;
        payload.map_marker_mode = form.map_marker_mode;
      }
      await api.put(`/fleet/devices/${device.id}/asset`, payload);
      toast.success('Device updated');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update device');
    } finally {
      setSaving(false);
    }
  };

  if (!device) return null;

  const tabs = [];
  if (showAssetTab) tabs.push({ id: 'asset', label: 'Asset Type' });
  if (showIconTab) tabs.push({ id: 'icon', label: 'Icon' });
  if (tabs.length === 0) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md" data-testid="edit-device-dialog-no-access">
          <DialogHeader>
            <DialogTitle>Edit Device — {deviceDisplayName(device)}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-4">
            You do not have permission to edit this device. Contact your administrator to enable editing privileges.
          </p>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogContent>
      </Dialog>
    );
  }

  const d = freshDevice || device;
  const at = form.asset_type;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[85vh] p-0 flex flex-col" data-testid="edit-device-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
          <DialogTitle>Edit Device — {deviceDisplayName(d)}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#2563eb]" />
          </div>
        ) : (
          <>
            {/* Tab buttons — fixed */}
            {tabs.length > 1 && (
              <div className="flex gap-1 px-6 border-b shrink-0">
                {tabs.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === t.id
                        ? 'border-b-2 border-[#2563eb] text-[#2563eb]'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`edit-tab-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}

            {/* Scrollable content area — matches Device Management structure.
                [&_...viewport]>div]:!block overrides Radix's display:table so
                content can wrap on mobile instead of clipping off-screen. */}
            <ScrollArea className="flex-1 px-6 py-4 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!block">

            {/* Asset Type Tab — matches Device Management exactly */}
            {activeTab === 'asset' && showAssetTab && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Asset Name / Unit Number</Label>
                  <Input value={form.asset_name} onChange={e => set('asset_name', e.target.value)}
                    placeholder={isBeacon ? 'John Smith, Trailer T-01, Tool Box A' : 'Truck 14, Excavator #3'} data-testid="edit-asset-name" autoComplete="off" />
                  <p className="text-xs text-muted-foreground">
                    {isBeacon ? 'The person, asset, or object this beacon is attached to' : 'The real-world name for the vehicle or asset this device is attached to'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Asset Type</Label>
                  <Select value={form.asset_type} onValueChange={v => setForm(prev => {
                    const wasAuto = !prev.device_icon || prev.device_icon === assetTypeIcon(prev.asset_type);
                    return { ...prev, asset_type: v, device_icon: wasAuto ? assetTypeIcon(v) : prev.device_icon };
                  })}>
                    <SelectTrigger data-testid="edit-asset-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(isBeacon ? beaconAssetTypes : assetTypes).map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Context-specific asset fields — dynamic based on asset_type */}
                {!isBeacon && VEHICLE_TYPES.includes(at) && (
                  <>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2"><Label>Make</Label>
                        <Input value={form.asset_make} onChange={e => set('asset_make', e.target.value)} placeholder="Toyota" autoComplete="off" /></div>
                      <div className="space-y-2"><Label>Model</Label>
                        <Input value={form.asset_model} onChange={e => set('asset_model', e.target.value)} placeholder="Hilux" autoComplete="off" /></div>
                      <div className="space-y-2"><Label>Year</Label>
                        <Input value={form.asset_year} onChange={e => set('asset_year', e.target.value)} placeholder="2024" autoComplete="off" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Registration Number</Label>
                        <Input value={form.registration} onChange={e => set('registration', e.target.value)} placeholder="ABC123" autoComplete="off" /></div>
                      <div className="space-y-2"><Label>VIN Number</Label>
                        <Input value={form.vin_number} onChange={e => set('vin_number', e.target.value)} placeholder="1HGBH41JXMN109186" className="font-mono" autoComplete="off" /></div>
                    </div>
                  </>
                )}

                {!isBeacon && at === 'ebike' && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2"><Label>Make</Label>
                      <Input value={form.asset_make} onChange={e => set('asset_make', e.target.value)} placeholder="Specialized" autoComplete="off" /></div>
                    <div className="space-y-2"><Label>Model</Label>
                      <Input value={form.asset_model} onChange={e => set('asset_model', e.target.value)} placeholder="Turbo Levo" autoComplete="off" /></div>
                    <div className="space-y-2"><Label>Year</Label>
                      <Input value={form.asset_year} onChange={e => set('asset_year', e.target.value)} placeholder="2024" autoComplete="off" /></div>
                    <div className="col-span-3 space-y-2"><Label>Serial Number</Label>
                      <Input value={form.serial_no} onChange={e => set('serial_no', e.target.value)} placeholder="WSBC123456789" className="font-mono" autoComplete="off" /></div>
                  </div>
                )}

                {!isBeacon && at === 'container' && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2"><Label>Container Number</Label>
                      <Input value={form.container_number} onChange={e => set('container_number', e.target.value)} placeholder="MSCU1234567" className="font-mono" autoComplete="off" /></div>
                    <div className="space-y-2"><Label>Size</Label>
                      <Select value={form.container_size || ''} onValueChange={v => set('container_size', v)}>
                        <SelectTrigger><SelectValue placeholder="Select size" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="20ft">20ft</SelectItem><SelectItem value="40ft">40ft</SelectItem><SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select></div>
                    <div className="space-y-2"><Label>Type</Label>
                      <Select value={form.container_type || ''} onValueChange={v => set('container_type', v)}>
                        <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dry">Dry</SelectItem><SelectItem value="reefer">Reefer</SelectItem>
                          <SelectItem value="open_top">Open Top</SelectItem><SelectItem value="flat_rack">Flat Rack</SelectItem>
                        </SelectContent>
                      </Select></div>
                  </div>
                )}

                {!isBeacon && at === 'personnel' && (
                  <div className="space-y-4">
                    <div className="space-y-2"><Label>Name</Label>
                      <Input value={form.asset_make} onChange={e => set('asset_make', e.target.value)} placeholder="Person's name" autoComplete="off" /></div>
                    <div className="space-y-2"><Label>Description</Label>
                      <Textarea value={form.asset_description} onChange={e => set('asset_description', e.target.value)} placeholder="Additional details..." rows={3} /></div>
                  </div>
                )}

                {!isBeacon && at === 'other' && (
                  <div className="space-y-2"><Label>Description</Label>
                    <Textarea value={form.asset_description} onChange={e => set('asset_description', e.target.value)} placeholder="Describe this asset..." rows={3} /></div>
                )}

                {/* Fuel Type */}
                {!isBeacon && VEHICLE_TYPES.includes(at) && (
                  <div className="space-y-2">
                    <Label>Fuel Type</Label>
                    <Select value={form.fuel_type || ''} onValueChange={v => set('fuel_type', v)}>
                      <SelectTrigger><SelectValue placeholder="Select fuel type" /></SelectTrigger>
                      <SelectContent>
                        {fuelTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Fuel configuration — shown when the admin enabled a Fuel Source */}
                {!isBeacon && VEHICLE_TYPES.includes(at) && d.fuel_source && d.fuel_source !== 'off' && (
                  <div className="p-3 border rounded-lg space-y-3" data-testid="edit-fuel-section">
                    <p className="font-medium text-sm">
                      Fuel {d.fuel_source === 'sensor' ? '(Sensor)' : '(Estimate)'}
                    </p>
                    {d.fuel_source === 'sensor' && (d.sensor_fuel_level != null || d.sensor_ev_soc != null) && (
                      <div className="p-2 bg-muted rounded-lg flex items-center justify-between text-sm" data-testid="edit-fuel-current-level">
                        <span className="text-muted-foreground">Current level</span>
                        <span className="font-medium">
                          {d.sensor_fuel_level != null ? d.sensor_fuel_level : d.sensor_ev_soc}
                          {(() => { const m = (d.io_mappings || []).find(x => x.io_type === 'Fuel Level' || x.io_type === 'EV Battery Level'); const u = m?.units; return u && u !== 'None' ? ` ${u}` : ''; })()}
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      {form.fuel_type !== 'electric' && (
                        <div className="space-y-2">
                          <Label>Tank Capacity (L)</Label>
                          <Input type="number" min="0" step="0.1" data-testid="edit-tank-capacity-input"
                            value={form.tank_capacity} onChange={e => set('tank_capacity', e.target.value)} placeholder="e.g. 60" autoComplete="off" />
                        </div>
                      )}
                      {(form.fuel_type === 'electric' || form.fuel_type === 'hybrid') && (
                        <div className="space-y-2">
                          <Label>Battery Capacity (kWh)</Label>
                          <Input type="number" min="0" step="0.1" data-testid="edit-battery-capacity-input"
                            value={form.battery_capacity_kwh} onChange={e => set('battery_capacity_kwh', e.target.value)} placeholder="e.g. 64" autoComplete="off" />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label>Reference Consumption ({form.fuel_type === 'electric' ? 'kWh/100km' : 'L/100km'})</Label>
                        <Input type="number" min="0" step="0.1" data-testid="edit-reference-consumption-input"
                          value={form.fuel_reference_consumption} onChange={e => set('fuel_reference_consumption', e.target.value)} placeholder="e.g. 8.5" autoComplete="off" />
                        <p className="text-xs text-muted-foreground">Used by the estimate engine on the Fleet &gt; Fuel page</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Fuel Price ({form.fuel_type === 'electric' ? '$/kWh' : '$/L'})</Label>
                        <Input type="number" min="0" step="0.001" data-testid="edit-fuel-price-input"
                          value={form.fuel_price} onChange={e => set('fuel_price', e.target.value)} placeholder="Global default" autoComplete="off" />
                        <p className="text-xs text-muted-foreground">Leave blank to use the global default price</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Odometer & Engine Hours — hidden entirely when the admin has the source OFF */}
                {!isBeacon && ODO_TYPES.includes(at) && (d.odometer_source !== 'off' || d.engine_hours_source !== 'off') && (
                  <div className="grid grid-cols-2 gap-4">
                    {d.odometer_source !== 'off' && (
                    <div className="space-y-2">
                      <Label>Odometer Reading (km)</Label>
                      <Input type="number" data-testid="edit-odometer-input"
                        value={form.odometer_reading}
                        onChange={e => set('odometer_reading', e.target.value)}
                        placeholder="0"
                        disabled={d.odometer_source === 'sensor'}
                        autoComplete="off"
                      />
                      <p className="text-xs text-muted-foreground">
                        {d.odometer_source === 'gps' && 'Editable — calculated from GPS data'}
                        {d.odometer_source === 'sensor' && 'Read-only — sourced from CAN Bus sensor'}
                      </p>
                    </div>
                    )}
                    {d.engine_hours_source !== 'off' && (
                    <div className="space-y-2">
                      <Label>Engine Hours</Label>
                      <Input type="number" step="0.1" data-testid="edit-engine-hours-input"
                        value={form.engine_hours}
                        onChange={e => set('engine_hours', e.target.value)}
                        placeholder="0"
                        disabled={d.engine_hours_source === 'sensor'}
                        autoComplete="off"
                      />
                      <p className="text-xs text-muted-foreground">
                        {d.engine_hours_source === 'ignition' && 'Editable — counted when ignition is ON'}
                        {d.engine_hours_source === 'sensor' && 'Read-only — sourced from CAN Bus sensor'}
                      </p>
                    </div>
                    )}
                  </div>
                )}

                {/* Timezone Override — matches Device Management exactly */}
                <div className="p-3 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm flex items-center gap-2"><Clock className="w-4 h-4" /> Timezone Override</p>
                      <p className="text-xs text-muted-foreground">Override all timezone settings for this device. Useful when a device operates in a different state or country.</p>
                    </div>
                    <Switch
                      checked={form.timezone_override_enabled || false}
                      onCheckedChange={v => {
                        set('timezone_override_enabled', v);
                        if (v && !form.timezone_override) set('timezone_override', 'Australia/Sydney');
                        if (!v) set('timezone_override', null);
                      }}
                    />
                  </div>
                  {form.timezone_override_enabled && (
                    <div className="space-y-2">
                      <TimezoneSelector
                        value={form.timezone_override || 'Australia/Sydney'}
                        onChange={v => set('timezone_override', v)}
                        includeAuto={true}
                      />
                      <div className="p-2 bg-muted rounded-lg flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Current time at device:</span>
                        <span className="font-mono font-medium">
                          {form.timezone_override === 'auto' ? (
                            <span className="text-amber-600">Auto — timezone detected from GPS location</span>
                          ) : (() => {
                            try {
                              const tz = form.timezone_override || 'Australia/Sydney';
                              const now = new Date();
                              const time = now.toLocaleString('en-AU', { timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, weekday: 'short', day: '2-digit', month: 'short' });
                              const offset = now.toLocaleString('en-AU', { timeZone: tz, timeZoneName: 'shortOffset' }).split(' ').pop();
                              return `${time} (${offset})`;
                            } catch { return '-'; }
                          })()}
                        </span>
                      </div>
                      {form.timezone_override === 'auto' && d.timezone_auto && (
                        <div className="p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-sm">
                          <span className="text-muted-foreground">Detected timezone: </span>
                          <span className="font-mono font-medium text-blue-600">{d.timezone_auto}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Icon Tab */}
            {activeTab === 'icon' && showIconTab && (
              <div className="space-y-3">
                {/* Sticky: Toggle + Icon Preview + Save + Color + Search */}
                <div className="sticky -top-4 z-10 bg-background pt-4 pb-3 space-y-3 border-b">
                  {/* Map Marker Mode Toggle */}
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">Use Icon on Map</p>
                      <p className="text-xs text-muted-foreground">
                        {form.map_marker_mode === 'icon'
                          ? 'This device shows its icon on the map'
                          : 'This device shows a directional arrow on the map'}
                      </p>
                    </div>
                    <Switch
                      data-testid="map-marker-mode-toggle"
                      checked={form.map_marker_mode === 'icon'}
                      onCheckedChange={(checked) => set('map_marker_mode', checked ? 'icon' : 'arrow')}
                    />
                  </div>
                  <div className="flex items-center gap-4 p-3 border rounded-lg">
                    <DeviceIconDisplay iconName={form.device_icon} className="w-12 h-12" customIcons={customIcons} iconColor={form.device_icon_color} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium capitalize text-sm">{form.device_icon?.startsWith('custom_') ? (customIcons.find(c => c.id === form.device_icon)?.name || 'Custom Icon') : formatIconLabel(form.device_icon)}</p>
                      <p className="text-xs text-muted-foreground">Selected icon{form.device_icon_color ? ` \u00b7 ${form.device_icon_color}` : ''}</p>
                    </div>
                    <Button onClick={handleSave} disabled={saving} className="bg-[#2563eb] hover:bg-[#1d4ed8] shrink-0" data-testid="edit-device-save-btn">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Changes
                    </Button>
                  </div>
                  <IconColorPicker
                    selectedColor={form.device_icon_color}
                    onChange={(color) => set('device_icon_color', color)}
                    iconName={form.device_icon}
                  />
                  <IconSearchInput value={iconPickerSearch} onChange={setIconPickerSearch} />
                </div>
                <DeviceIconPicker
                  selectedIcon={form.device_icon}
                  onSelect={icon => {
                    set('device_icon', icon);
                    if (isCustomIcon(icon)) set('device_icon_color', null);
                  }}
                  customIcons={customIcons}
                  externalSearch={iconPickerSearch}
                  onExternalSearchChange={setIconPickerSearch}
                />
              </div>
            )}

            {/* Save Button — only for non-icon tabs */}
            {activeTab !== 'icon' && (
              <div className="flex justify-end pt-2 border-t">
                <Button onClick={handleSave} disabled={saving} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="edit-device-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            )}
            </ScrollArea>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
